import type { AxiosRequestConfig } from 'axios';
import { getMonitoring } from './monitoring.ts';
import { replacePath } from './monitoring-schema.utils.ts';
import type { MonitoringErrorTypes } from './monitoring.types.ts';

export const MONITORING_DOMAINS = [
  'AgOperations',
  'Company',
  'DiseaseRisk',
  'Methodologies',
  'Products',
  'Property',
  'Region',
  'RegionalOverview',
  'Resources',
  'Seeds',
  'Auth',
  'BorerRisk',
  'CLevelReport',
  'ControlStrategy',
  'CWElementsWrappers',
  'ErrorBoundary',
  'FieldBook',
  'FieldClustering',
  'FixedPoints',
  'I18n',
  'Integrations',
  'MapFeature',
  'NemaDigital',
  'OpportunityMachine',
  'RainDashboard',
  'Report',
  'Segment',
  'SmartSpray',
  'StaticPoints',
  'Stations',
  'Timeline',
  'VisitDashboard',
  'Warehouse',
  'AxiosInterceptor',
  'Architecture',
  'Zod',
  'API',
  'Generic'
] as const;

export type MonitoringDomain = (typeof MONITORING_DOMAINS)[number];

const MONITORING_DOMAIN_SET: ReadonlySet<string> = new Set<string>(MONITORING_DOMAINS);

const isMonitoringDomain = (value: string): value is MonitoringDomain => MONITORING_DOMAIN_SET.has(value);

const clampToCanonicalDomain = (tag: string): MonitoringDomain => (isMonitoringDomain(tag) ? tag : 'Generic');

type KnownErrorTag = 'ZodError' | 'APIError' | 'Generic Error';

type MonitoringErrorTag = KnownErrorTag | (string & NonNullable<unknown>);

export const KNOWN_ERROR_TAG_METADATA = {
  ZodError: { message: 'validation schema', domain: 'Zod', errorType: 'ValidationError' },
  APIError: { message: 'api error', domain: 'API', errorType: 'APIError' },
  'Generic Error': { message: 'generic error', domain: 'Generic', errorType: 'GenericError' }
} as const satisfies Record<KnownErrorTag, { message: string; domain: MonitoringDomain; errorType: MonitoringErrorTypes }>;

const isKnownErrorTag = (tag: string): tag is KnownErrorTag => Object.hasOwn(KNOWN_ERROR_TAG_METADATA, tag);

interface MonitoringErrorProps<T> {
  error: Record<string, T>;
  method: AxiosRequestConfig['method'];
  path: string;
  tag?: MonitoringErrorTag;
}

interface MonitoringErrorMetadata {
  name: string;
  message: string;
  domain: MonitoringDomain;
  errorType: MonitoringErrorTypes;
}

function getErrorMetadata(tag: MonitoringErrorTag): MonitoringErrorMetadata {
  return isKnownErrorTag(tag)
    ? { name: tag, ...KNOWN_ERROR_TAG_METADATA[tag] }
    : { name: tag, message: tag, domain: clampToCanonicalDomain(tag), errorType: tag };
}

export const monitoringError = <T>({ error, method, path, tag = 'Generic Error' }: MonitoringErrorProps<T>) => {
  const replacedPath = replacePath(path);
  const monitoring = getMonitoring();

  monitoring.setGlobalContextProperty(tag, { error });

  const { name, message, domain, errorType } = getErrorMetadata(tag);
  const errorInstance = new Error(message);
  errorInstance.name = name;
  errorInstance.stack = JSON.stringify(error);

  monitoring.captureError(errorInstance, {
    domain,
    errorType,
    error: JSON.stringify(error),
    isCrash: false,
    critical: false,
    source: 'custom',
    method,
    path: replacedPath
  });
};
